/**
 * ClickableMetric Component
 *
 * A wrapper that makes any metric value clickable to show evidence.
 * Displays a subtle indicator that the metric is explainable.
 *
 * Usage:
 *   <ClickableMetric uiFieldPath="current_finances.gross_income" runId={runId}>
 *     <div className="text-2xl">{formatCurrency(income)}</div>
 *   </ClickableMetric>
 *
 * BUG-UI-061: Added incomeBasis support for gross_income_6m comparison
 */

import { ReactNode } from 'react';
import { Info } from 'lucide-react';
import { useEvidenceModal, IncomeBasisData, DashboardValueData } from '@/context/EvidenceContext';
import { DisclosureLevel } from '@/types/evidenceContract';
import { getEvidenceConfig } from '@/config/uiEvidenceMap';
import { isHistoryFallbackMetricKey } from '@/common/scoreDisplay';

interface ClickableMetricProps {
  /** UI field path that maps to the evidence config (e.g., 'current_finances.gross_income') */
  uiFieldPath: string;
  /** The run_id from algo_runs table for this member's most recent calculation */
  runId: string;
  /** BUG-UI-092: Member ID for cross-run historical evidence */
  memberId?: string;
  /** Override the default disclosure level (defaults to LENDER) */
  disclosureLevel?: DisclosureLevel;
  /** Optional member name for display in modal */
  memberName?: string;
  /** The metric content to render */
  children: ReactNode;
  /** Additional CSS classes */
  className?: string;
  /** Whether to show the info icon indicator (default: true) */
  showIndicator?: boolean;
  /** Disable the click behavior (e.g., when no runId available) */
  disabled?: boolean;
  /** BUG-UI-061: Income basis data for gross_income_6m comparison */
  incomeBasis?: IncomeBasisData;
  /** BUG-UI-120: Dashboard value for reconciliation with modal display */
  dashboardValue?: DashboardValueData;
  /** Option 3: Initial month to display in modal (from card's selected month) */
  initialMonth?: string | null;
  /** BUG-UI-128: Custom tooltip text (default: "Click to see how this was calculated") */
  tooltipText?: string;
}

export function ClickableMetric({
  uiFieldPath,
  runId,
  memberId,
  disclosureLevel = 'LENDER',
  memberName,
  children,
  className = '',
  showIndicator = true,
  disabled = false,
  incomeBasis,
  dashboardValue,
  initialMonth,
  tooltipText = 'Click to see how this was calculated',
}: ClickableMetricProps) {
  const { showEvidence } = useEvidenceModal();

  // Check if this field has evidence configuration
  const evidenceConfig = getEvidenceConfig(uiFieldPath);

  // WP-8 Slice 8D: a metric is clickable when (a) it has an evidence config
  // and (b) either a runId is available OR a memberId is available and the
  // metric_key is in the lender-safe history-input fallback allowlist. This
  // removes the silent no-op for current-finances / trends rows that lacked
  // a top-level runId but have valid /history.inputs values to display.
  const canUseHistoryFallback =
    !!memberId &&
    !!evidenceConfig &&
    isHistoryFallbackMetricKey(evidenceConfig.metric_key);
  const hasEvidence =
    !!evidenceConfig && (!!runId || canUseHistoryFallback) && !disabled;

  // DEBUG: Log click eligibility
  if (import.meta.env.DEV) console.log(`🔍 [ClickableMetric] ${uiFieldPath}:`, {
    hasConfig: !!evidenceConfig,
    runId: runId || 'EMPTY',
    memberId: memberId || 'EMPTY',
    canUseHistoryFallback,
    disabled,
    hasEvidence,
  });

  const handleClick = () => {
    if (import.meta.env.DEV) console.log(`🖱️ [ClickableMetric] Click on ${uiFieldPath}, hasEvidence=${hasEvidence}, initialMonth=${initialMonth}`);
    if (!hasEvidence) return;

    // BUG-UI-061: Pass incomeBasis for gross_income_6m comparison
    // BUG-UI-092: Pass memberId for cross-run historical evidence
    // BUG-UI-120: Pass dashboardValue for reconciliation
    // Option 3: Pass initialMonth so modal opens at selected month
    if (import.meta.env.DEV) console.log(`🖱️ [ClickableMetric] Click with initialMonth="${initialMonth}" for ${uiFieldPath}`);
    showEvidence({
      uiFieldPath,
      runId,
      memberId,
      disclosureLevel,
      memberName,
      incomeBasis,
      dashboardValue,
      initialMonth,
    });
  };

  // WP-8 Slice 8D: when the metric has an evidence config but is currently
  // not clickable (no runId AND no fallback), render as a non-interactive
  // span with an accessible tooltip explaining why \u2014 prevents the silent
  // no-op that previously made cards appear broken to lenders.
  if (!hasEvidence) {
    if (evidenceConfig && !disabled) {
      return (
        <span
          className={`inline-flex items-center gap-1.5 text-left ${className}`}
          title="Detail not available for this metric yet."
          aria-label={`Evidence detail not available for ${evidenceConfig.label}`}
        >
          {children}
        </span>
      );
    }
    return <>{children}</>;
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (import.meta.env.DEV) console.log(`🔘 [ClickableMetric] Button clicked - ${uiFieldPath}`);
        handleClick();
      }}
      onMouseDown={(_e) => {
        if (import.meta.env.DEV) console.log(`⬇️ [ClickableMetric] MouseDown - ${uiFieldPath}`);
      }}
      className={`
        group relative inline-flex items-center gap-1.5
        cursor-pointer hover:opacity-80 transition-opacity
        text-left
        ${className}
      `}
      style={{ pointerEvents: 'auto' }}
      title={tooltipText}
      aria-label={`View evidence for ${evidenceConfig?.label || uiFieldPath}`}
    >
      {children}
      {showIndicator && (
        <span className="inline-flex opacity-0 group-hover:opacity-100 transition-opacity">
          <Info className="w-3.5 h-3.5 text-blue-500" />
        </span>
      )}
    </button>
  );
}

/**
 * Badge variant that shows a persistent "Explain" indicator
 */
export function ExplainableBadge({
  uiFieldPath,
  runId,
  disclosureLevel = 'LENDER',
  memberName,
  children,
  className = '',
}: Omit<ClickableMetricProps, 'showIndicator' | 'disabled'>) {
  const { showEvidence } = useEvidenceModal();
  const evidenceConfig = getEvidenceConfig(uiFieldPath);
  const hasEvidence = !!evidenceConfig && !!runId;

  const handleClick = () => {
    if (!hasEvidence) return;
    showEvidence({ uiFieldPath, runId, disclosureLevel, memberName });
  };

  if (!hasEvidence) {
    return <>{children}</>;
  }

  return (
    <div className={`relative ${className}`}>
      {children}
      <button
        onClick={handleClick}
        className="absolute -top-1 -right-1 px-1.5 py-0.5 text-[10px] font-medium bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors"
        title="Click to see evidence"
      >
        ?
      </button>
    </div>
  );
}

